// Master Badge Service
// Synchronizes Badge Definitions with Firestore (system/badges)
// Implements 31 Event-Specific & Universal Badges (Attending 1, 5, 10 & Hosting 1, 5, 10 for all 5 event types)

import { db } from './firebase';
import { doc, onSnapshot, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import type { BadgeDefinition, DadProfile, Mission, MaydayBeacon } from '../types';
import { initialBadges } from '../seedData';

export const BADGES_SCHEMA_VERSION = 'v2_per_event_badges';

type BadgesListener = (badges: BadgeDefinition[]) => void;
const listeners: BadgesListener[] = [];
let badgesCache: BadgeDefinition[] = [...initialBadges];

export function subscribeBadges(listener: BadgesListener): () => void {
  listeners.push(listener);
  listener(badgesCache);

  return () => {
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

function notifyListeners() {
  listeners.forEach(fn => fn(badgesCache));
}

export function getBadgesMasterList(): BadgeDefinition[] {
  return badgesCache;
}

let hasAttemptedBadgeSync = false;

// Initialize Firestore Real-time Listener for system/badges
export function initBadgesListener(): () => void {
  if (!db || !db.app) {
    return () => {};
  }

  const badgesDocRef = doc(db, 'system', 'badges');

  const unsubscribe = onSnapshot(badgesDocRef, async (snap) => {
    if (snap.exists()) {
      const data = snap.data();
      // Auto-migrate if schema version changed or badge count is not up to date
      if (Array.isArray(data.badges) && data.badges.length === initialBadges.length && data.version === BADGES_SCHEMA_VERSION) {
        badgesCache = data.badges as BadgeDefinition[];
        notifyListeners();
        return;
      }
    }

    if (hasAttemptedBadgeSync) {
      badgesCache = [...initialBadges];
      notifyListeners();
      return;
    }
    hasAttemptedBadgeSync = true;

    // Seed or update system/badges in Firestore
    try {
      await setDoc(badgesDocRef, {
        badges: initialBadges,
        version: BADGES_SCHEMA_VERSION,
        updatedAt: Date.now()
      });
      badgesCache = [...initialBadges];
      notifyListeners();
    } catch (err) {
      console.warn('[Pops Culture] Could not sync badges in Firestore, using initial:', err);
      badgesCache = [...initialBadges];
      notifyListeners();
    }
  }, (error) => {
    console.warn('[Pops Culture] Badges listener warning:', error);
    notifyListeners();
  });

  return unsubscribe;
}

// Award a badge to a user in Firestore
export async function awardBadge(userId: string, badgeId: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      badgesEarned: arrayUnion(badgeId)
    });
  } catch (err) {
    console.warn(`[Pops Culture] Failed to award badge ${badgeId} to user ${userId}:`, err);
  }
}

export interface BadgeProgressResult {
  isEarned: boolean;
  progressText: string;
  percentage: number;
}

// Calculate real live progress for a badge based on database records
export function calculateBadgeProgress(
  badge: BadgeDefinition,
  profile: DadProfile | null,
  missions: Mission[],
  _maydays: MaydayBeacon[] = []
): BadgeProgressResult {
  if (!profile) {
    return { isEarned: false, progressText: 'Sign in to track progress', percentage: 0 };
  }

  const earnedInProfile = Array.isArray(profile.badgesEarned) && profile.badgesEarned.includes(badge.id);

  if (badge.actionType === 'first_rsvp') {
    // First dad to RSVP (index 1 in enlisted, after index 0 which is host)
    const firstRsvpMission = missions.find(m => m.enlisted.length >= 2 && m.enlisted[1].userId === profile.uid);
    const isEarned = earnedInProfile || !!firstRsvpMission;
    return {
      isEarned,
      progressText: isEarned ? 'Completed' : 'First RSVP on any event',
      percentage: isEarned ? 100 : 0
    };
  }

  if (badge.actionType === 'attend') {
    const targetType = badge.eventType;
    const target = badge.threshold || 1;
    // Count events user attended as a participant (not as host)
    const count = missions.filter(m => {
      const matchType = m.type === targetType || (targetType === 'gaming' && m.type === 'sports_gaming');
      return matchType && m.enlisted.some(e => e.userId === profile.uid && e.roleType !== 'host');
    }).length;

    const isEarned = earnedInProfile || count >= target;
    return {
      isEarned,
      progressText: isEarned ? 'Completed' : `${count} of ${target} Attended`,
      percentage: Math.min(100, Math.round((count / target) * 100))
    };
  }

  if (badge.actionType === 'host') {
    const targetType = badge.eventType;
    const target = badge.threshold || 1;
    // Count events user hosted
    const count = missions.filter(m => {
      const matchType = m.type === targetType || (targetType === 'gaming' && m.type === 'sports_gaming');
      const isHost = m.host.userId === profile.uid || m.enlisted.some(e => e.userId === profile.uid && e.roleType === 'host');
      return matchType && isHost;
    }).length;

    const isEarned = earnedInProfile || count >= target;
    return {
      isEarned,
      progressText: isEarned ? 'Completed' : `${count} of ${target} Hosted`,
      percentage: Math.min(100, Math.round((count / target) * 100))
    };
  }

  return {
    isEarned: earnedInProfile,
    progressText: earnedInProfile ? 'Completed' : 'Locked',
    percentage: earnedInProfile ? 100 : 0
  };
}

// Automatically check and award badges if criteria have been achieved
export async function checkAndAutoAwardBadges(
  profile: DadProfile,
  missions: Mission[],
  maydays: MaydayBeacon[]
): Promise<void> {
  if (!profile || !profile.uid) return;

  const currentEarned = new Set(profile.badgesEarned || []);
  let hasNewBadge = false;

  for (const badge of badgesCache) {
    if (!currentEarned.has(badge.id)) {
      const result = calculateBadgeProgress(badge, profile, missions, maydays);
      if (result.isEarned) {
        currentEarned.add(badge.id);
        hasNewBadge = true;
        await awardBadge(profile.uid, badge.id);
      }
    }
  }

  if (hasNewBadge) {
    profile.badgesEarned = Array.from(currentEarned);
  }
}
